export enum UserDeliveryRequirements {
  IN_PERSON = 'IN_PERSON',
  CONTACTLESS = 'CONTACTLESS'
}

export enum DeliveryStatusType {
  PENDING = 'PENDING',
  ASSIGNED = 'ASSIGNED',
  SCANNED = 'SCANNED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

export enum PackageStatusType {
  BROKEN = 'BROKEN',
  DELIVERED = 'DELIVERED',
  IN_PROGRESS = 'IN_PROGRESS',
  RETURNED_TO_MERCHANT = 'RETURNED_TO_MERCHANT',
  AT_HUB = 'AT_HUB',
  CANCELLED = 'CANCELLED',
  BOOKED = 'BOOKED',
  PREBOOKED = 'PREBOOKED',
  LOST = 'LOST'
}

export enum EntryType {
  DOORCODE = 'DOORCODE',
  INTERCOM = 'INTERCOM'
}

export enum DeliveryNotificationType {
  SEND_SMS = 'SEND_SMS',
  RING_DOOR_BELL = 'RING_DOOR_BELL',
  QUIET = 'QUIET',
  NONE = 'NONE'
}

export enum DeliveryVerificationMethod {
  BANKID_QR = 'BANKID_QR',
  DRIVING_LICENSE_QR = 'DRIVING_LICENSE_QR',
  BANKID_CODE = 'BANKID_CODE'
}

export type Package = {
  userDeliveryRequirements: UserDeliveryRequirements;
  status?: PackageStatusType;
  internalPackageId: string;
  externalPackageId: string;
  originalCompleteBefore?: Date | null;
  originalCompleteAfter?: Date | null;
  deliveryNotificationType?: DeliveryNotificationType | null;
};

export type Task = {
  completedAt?: Date | null;
  completeBefore?: Date | null;
  completeAfter?: Date | null;
  taskInstructions?: string | null;
  status?: DeliveryStatusType;
  consumerUpdatedInfoAt: Date | null;
  identificationCheckRequired?: boolean | null;
  recipientMinimumAge?: number | null;
  recipientMustMatchEndCustomer?: boolean | null;
  requireSignature?: boolean | null;
  preOrderRequired?: boolean | null;
  id: string;
  nameOnDoor?: string | null;
  verificationCode?: string | null;
  trackingLinkOpenedAt?: Date | null;
};

export type StreetAddress = {
  latitude?: number;
  longitude?: number;
};

export type Recipient = {
  floorNo?: string | null;
  intercom?: string | null;
  doorCode?: string | null;
  status: DeliveryStatusType;
  entryOption?: EntryType | null;
};

export type Partner = {
  name: string;
  countryCode?: string;
};

export type Route = {
  id: string;
  status?: DeliveryStatusType;
  shiftId: string | null;
};

export type ETA = {
  lowerBound: Date | null;
  upperBound: Date | null;
  predictedArrival: Date | null;
  originalPredictedArrival: Date | null;
};

export type FeedbackSuggestion = {
  key: number;
  rates: number[];
};

export type Setting = {
  customCSS: string;
  brand: {
    color: string;
    favicon: string;
    riderIcon: string;
    userIcon: string;
  };
  floorName: {
    ground_floor: string;
    basement: string;
    sub_basement: string;
  };
  feedbackSuggestions: FeedbackSuggestion[];
  locales: object;
  displayDeliveryConfirmationPhotos: boolean;
  trackingUrl: string;
  enableCustomerSupportForSuccessfulDeliveries: boolean;
  displayName: string;
  deliveryVerificationMethods: Record<string, any> | null;
};

export type AppState = {
  partner?: Partner;
  settings?: Setting;
  trackingDetails?: {
    recipient?: Recipient;
    streetAddress?: StreetAddress;
    task?: Task;
    package?: Package;
    route?: Route;
    lastKnownLatitude?: number;
    lastKnownLongitude?: number;
    bearing: number;
    stopsAhead: number;
    eta: ETA;
    feedbackId?: string | null;
    rider?: {
      name?: string | null;
    };
    hubName?: string | null;
    verifiedPhotoUrls: string[];
    trackingEvents: { eventNo: number; datetime: Date }[];
    announcement?: string | null;
  };
  isCustomerInputsOpen: boolean;
  bankId: {
    orderRef: string | null;
  };
  isPolling: boolean;
  setPartnerAndSettings: (data: any) => void;
  setTrackingDetails: (data: any) => void;
  setCustomerInputsOpen: () => void;
  setBankIdOderRef: (data: any) => void;
  setPollingStatus: () => void;
};

export enum AppAction {
  SET_PARTNER_AND_SETTINGS = 'SET_PARTNER_AND_SETTINGS',
  SET_TRACKING_DETAILS = 'SET_TRACKING_DETAILS',
  SET_CUSTOMER_INPUTS_OPEN = 'SET_CUSTOMER_INPUTS_OPEN',
  SET_BANKID_ORDER_REF = 'SET_BANKID_ORDER_REF',
  SET_POLLING_STATUS = 'SET_POLLING_STATUS'
}
